import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import React from 'react';
import { arrayOf, shape, string, bool } from 'prop-types';
import defaultClasses from './video.module.css';
import { useMergeCssClasses } from '../../Utils/utils';
/**
 * Page Builder Video component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Video
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays a Video using an iframe.
 */

var Video = function Video(props) {
  var classes = useMergeCssClasses(defaultClasses, props.classes);
  var _props$url = props.url,
      url = _props$url === void 0 ? '' : _props$url,
      autoplay = props.autoplay,
      muted = props.muted,
      maxWidth = props.maxWidth,
      textAlign = props.textAlign,
      border = props.border,
      borderColor = props.borderColor,
      borderWidth = props.borderWidth,
      borderRadius = props.borderRadius,
      marginTop = props.marginTop,
      marginRight = props.marginRight,
      marginBottom = props.marginBottom,
      marginLeft = props.marginLeft,
      paddingTop = props.paddingTop,
      paddingRight = props.paddingRight,
      paddingBottom = props.paddingBottom,
      paddingLeft = props.paddingLeft,
      videoName = props.videoName,
      activeVideoName = props.activeVideoName,
      resourceName = props.resourceName,
      _props$cssClasses = props.cssClasses,
      cssClasses = _props$cssClasses === void 0 ? [] : _props$cssClasses;
  var mainStyles = {
    textAlign: textAlign,
    marginTop: marginTop,
    marginRight: marginRight,
    marginBottom: marginBottom,
    marginLeft: marginLeft
  };
  var innerStyles = {
    maxWidth: maxWidth
  };
  var wrapperStyles = {
    border: border,
    borderColor: borderColor,
    borderWidth: borderWidth,
    borderRadius: borderRadius,
    paddingTop: paddingTop,
    paddingRight: paddingRight,
    paddingBottom: paddingBottom,
    paddingLeft: paddingLeft
  };
  var youtubeRegExp = new RegExp('^(?:https?://|//)?(?:www\\.|m\\.)?' + '(?:youtu\\.be/|youtube\\.com/(?:embed/|v/|watch\\?v=|watch\\?.+&v=))([\\w-]{11})(?![\\w-])');
  var vimeoRegExp = new RegExp('https?://(?:www\\.|player\\.)?vimeo.com/(?:channels/' + '(?:\\w+/)?|groups/([^/]*)/videos/|album/(\\d+)/video/|video/|)(\\d+)(?:$|/|\\?)');
  var Video = '';
  var splitUrl = url === null || url === void 0 ? void 0 : url.split('?');
  var searchParams = new URLSearchParams(splitUrl === null || splitUrl === void 0 ? void 0 : splitUrl[1]);
  searchParams.set('controls', 0);
  var searchParamsUrl = (splitUrl === null || splitUrl === void 0 ? void 0 : splitUrl[0]) + '?' + (searchParams === null || searchParams === void 0 ? void 0 : searchParams.toString());

  if (searchParamsUrl && searchParamsUrl.length && (youtubeRegExp.test(searchParamsUrl) || vimeoRegExp.test(searchParamsUrl))) {
    Video = React.createElement("div", {
      className: classes.container
    }, React.createElement("iframe", {
      className: classes.video,
      title: "".concat(videoName || activeVideoName || resourceName, " video"),
      frameBorder: "0",
      allowFullScreen: "1",
      loading: "lazy",
      src: url
    }));
  } else if (url && url.length) {
    /* eslint-disable jsx-a11y/media-has-caption */
    Video = React.createElement("div", {
      className: classes.container
    }, React.createElement("video", {
      className: classes.video,
      src: url,
      autoPlay: autoplay,
      muted: muted,
      frameBorder: "0",
      controls: true
    }));
    /* eslint-enable jsx-a11y/media-has-caption */
  }

  return React.createElement("div", {
    style: mainStyles,
    className: [classes.root].concat(_toConsumableArray(cssClasses)).join(' ')
  }, React.createElement("div", {
    style: innerStyles,
    className: classes.inner
  }, React.createElement("div", {
    style: wrapperStyles,
    className: classes.wrapper
  }, Video)));
};
/**
 * Props for {@link Video}
 *
 * @typedef props
 *
 * @property {Object} classes An object containing the class names for the Video
 * @property {String} classes.root CSS classes for the root container element
 * @property {String} classes.inner CSS classes for the inner container element
 * @property {String} classes.wrapper CSS classes for the wrapper container element
 * @property {String} classes.container CSS classes for the container element
 * @property {String} classes.video CSS classes for the video element
 * @property {String} url URL to render the video from an external provider (YouTube, Vimeo etc)
 * @property {Boolean} autoplay Video autoplay
 * @property {Boolean} muted Video muted
 * @property {String} maxWidth Maximum width of the video
 * @property {String} textAlign Alignment of the video within the parent container
 * @property {String} border CSS border property
 * @property {String} borderColor CSS border color property
 * @property {String} borderWidth CSS border width property
 * @property {String} borderRadius CSS border radius property
 * @property {String} marginTop CSS margin top property
 * @property {String} marginRight CSS margin right property
 * @property {String} marginBottom CSS margin bottom property
 * @property {String} marginLeft CSS margin left property
 * @property {String} paddingTop CSS padding top property
 * @property {String} paddingRight CSS padding right property
 * @property {String} paddingBottom CSS padding bottom property
 * @property {String} paddingLeft CSS padding left property
 * @property {Array} cssClasses List of CSS classes to be applied to the component
 */


Video.propTypes = {
  classes: shape({
    root: string,
    inner: string,
    wrapper: string,
    container: string,
    video: string
  }),
  url: string,
  autoplay: bool,
  muted: bool,
  maxWidth: string,
  textAlign: string,
  border: string,
  borderColor: string,
  borderWidth: string,
  borderRadius: string,
  marginTop: string,
  marginRight: string,
  marginBottom: string,
  marginLeft: string,
  paddingTop: string,
  paddingRight: string,
  paddingBottom: string,
  cssClasses: arrayOf(string)
};
export default Video;